import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Key, Copy, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";



export default function ApiPage() {
  const [tab, setTab] = useState("keys");
  const [keys, setKeys] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newKey, setNewKey] = useState(null);
  const confirm = useConfirm();

  const fetchKeys = async () => { try { const r = await API.get("/api-keys"); setKeys(r.data); } catch {} };
  useEffect(() => { if (tab === "keys") fetchKeys(); }, [tab]);

  const createKey = async () => {
    try {
      const r = await API.post("/api-keys", { label: newLabel || "Untitled Key" });
      setNewKey(r.data.key);
      setNewLabel("");
      fetchKeys();
    } catch { toast.error("Failed"); }
  };

  const revokeKey = async (keyId) => {
    if (!(await confirm("Revoke API Key", "This key will stop working immediately. This cannot be undone."))) return;
    await API.delete("/api-keys/" + keyId);
    toast.success("Key revoked");
    fetchKeys();
  };

  const copyKey = (key) => {
    try {
      if (navigator.clipboard) { navigator.clipboard.writeText(key).then(() => toast.success("Copied to clipboard")).catch(() => fallbackCopy(key)); }
      else { fallbackCopy(key); }
    } catch { fallbackCopy(key); }
  };
  const fallbackCopy = (text) => {
    const ta = document.createElement("textarea"); ta.value = text; ta.style.cssText = "position:fixed;left:-9999px";
    document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    toast.success("Copied to clipboard");
  };


  return (
    <div data-testid="api-page">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-[28px] font-bold text-[#37352F]">API</h1>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-8 bg-[#E8E8E6] rounded-[4px] p-0.5">
            <TabsTrigger value="keys" className="text-[12px] h-7 px-3 rounded-[3px] data-[state=active]:bg-white data-[state=active]:shadow-sm">API Keys</TabsTrigger>
            <TabsTrigger value="docs" className="text-[12px] h-7 px-3 rounded-[3px] data-[state=active]:bg-white data-[state=active]:shadow-sm">Documentation</TabsTrigger>
            <TabsTrigger value="mcp" className="text-[12px] h-7 px-3 rounded-[3px] data-[state=active]:bg-white data-[state=active]:shadow-sm">Claude MCP</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <p className="text-[13px] text-[#6B6B6B] mb-6">Manage API keys and explore the REST API</p>

      {tab === "keys" && (<>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[16px] font-semibold text-[#37352F]">Your API Keys</h2>
          <Button onClick={() => { setShowCreate(true); setNewKey(null); }} size="sm" className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white text-[12px] h-7"><Plus className="w-3.5 h-3.5 mr-1"/> Generate Key</Button>
        </div>

        <div className="bg-[#F7F6F3] border border-[#E0E0DE] rounded-[4px] p-3 mb-4 text-[13px] text-[#6B6B6B]">
          <p>Use API keys to authenticate requests. Include the key in the <code className="bg-[#E8E8E6] px-1 rounded text-[12px]">X-API-Key</code> header:</p>
          <pre className="mt-2 bg-[#37352F] text-[#E8E8E6] rounded-[4px] px-3 py-2 text-[12px] font-mono overflow-x-auto">curl -H "X-API-Key: gdk_your_key_here" {window.location.origin}/api/tasks/flat</pre>
        </div>

        {keys.length === 0 ? (
          <div className="text-center py-12"><Key className="w-8 h-8 text-[#B8B6B0] mx-auto mb-3" strokeWidth={1}/><p className="text-[#6B6B6B] text-[14px]">No API keys yet</p></div>
        ) : (
          <div className="space-y-2">
            {keys.map(k => (
              <div key={k.key_id} className="flex items-center gap-3 p-3 border border-[#E0E0DE] rounded-[4px] hover:bg-[#F7F6F3]/50 group">
                <Key className="w-4 h-4 text-[#6B6B6B] shrink-0"/>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-[#37352F]">{k.label}</p>
                  <p className="text-[12px] text-[#6B6B6B] font-mono">{k.key_masked}</p>
                </div>
                <div className="text-[11px] text-[#6B6B6B] text-right shrink-0">
                  <p>Created {new Date(k.created_at).toLocaleDateString()}</p>
                  {k.last_used && <p>Last used {new Date(k.last_used).toLocaleDateString()}</p>}
                </div>
                <button onClick={() => revokeKey(k.key_id)} className="text-[#B8B6B0] hover:text-[#EB5757] p-1 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-4 h-4"/></button>
              </div>
            ))}
          </div>
        )}

        {/* Create key dialog */}
        <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setNewKey(null); }}>
          <DialogContent className="bg-white border-[#E0E0DE] shadow-sm">
            <DialogHeader><DialogTitle className="text-[15px] font-semibold text-[#37352F]">{newKey ? "API Key Created" : "Generate API Key"}</DialogTitle></DialogHeader>
            {!newKey ? (
              <div>
                <Label className="text-[12px] text-[#6B6B6B]">Label</Label>
                <Input value={newLabel} onChange={e => setNewLabel(e.target.value)} onKeyDown={e => e.key === "Enter" && createKey()} placeholder="e.g., My Integration" className="mt-1 bg-transparent border-[#E0E0DE] text-[14px]" autoFocus/>
                <DialogFooter className="mt-3"><Button onClick={createKey} className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white text-[13px] h-8">Generate</Button></DialogFooter>
              </div>
            ) : (
              <div>
                <p className="text-[13px] text-[#6B6B6B] mb-2">Copy this key now — you won't be able to see it again.</p>
                <div className="flex items-center gap-2 p-2 bg-[#F7F6F3] border border-[#E0E0DE] rounded-[4px]">
                  <code className="flex-1 text-[13px] font-mono text-[#37352F] break-all">{newKey}</code>
                  <button onClick={() => copyKey(newKey)} className="text-[#2383E2] hover:text-[#1B6EC2] p-1 shrink-0"><Copy className="w-4 h-4"/></button>
                </div>
                <DialogFooter className="mt-3"><Button onClick={() => { setShowCreate(false); setNewKey(null); }} className="bg-[#37352F] hover:bg-[#1F1E1B] text-white text-[13px] h-8">Done</Button></DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </>)}

      {tab === "docs" && (<>
        <div className="bg-[#F7F6F3] border border-[#E0E0DE] rounded-[4px] p-3 mb-4 text-[13px] text-[#6B6B6B]">
          <p>Interactive API documentation powered by Swagger/OpenAPI. <a href={window.location.origin + "/api/docs"} target="_blank" rel="noopener noreferrer" className="text-[#2383E2] hover:underline">Open in new tab</a></p>
        </div>
        <div className="border border-[#E0E0DE] rounded-[4px] overflow-hidden" style={{height: "calc(100vh - 240px)"}}>
          <iframe src={window.location.origin + "/api/docs"} className="w-full h-full border-none" title="API Documentation" />
        </div>
      </>)}

      {tab === "mcp" && (<>
        <div className="max-w-[800px]">
          <h2 className="text-[16px] font-semibold text-[#37352F] mb-3">Claude Desktop MCP Integration</h2>
          <p className="text-[13px] text-[#6B6B6B] mb-4">Connect Claude to your GTD tool so it can manage your goals, projects, tasks, rosters, and habits.</p>

          <div className="space-y-4">
            <div className="border border-[#E0E0DE] rounded-[4px] p-4">
              <h3 className="text-[14px] font-medium text-[#37352F] mb-2">Step 1: Generate an API Key</h3>
              <p className="text-[13px] text-[#6B6B6B]">Go to the <button onClick={() => setTab("keys")} className="text-[#2383E2] hover:underline">API Keys tab</button> and generate a key. Copy it — you'll need it in Step 3.</p>
            </div>

            <div className="border border-[#E0E0DE] rounded-[4px] p-4">
              <h3 className="text-[14px] font-medium text-[#37352F] mb-2">Step 2: Install Dependencies</h3>
              <pre className="bg-[#37352F] text-[#E8E8E6] rounded-[4px] px-3 py-2 text-[12px] font-mono overflow-x-auto">pip install mcp httpx</pre>
            </div>

            <div className="border border-[#E0E0DE] rounded-[4px] p-4">
              <h3 className="text-[14px] font-medium text-[#37352F] mb-2">Step 3: Download the MCP Server</h3>
              <p className="text-[13px] text-[#6B6B6B] mb-2">Save this file somewhere on your machine (e.g., <code className="bg-[#E8E8E6] px-1 rounded text-[12px]">~/gtd_mcp_server.py</code>):</p>
              <Button onClick={() => { window.open(window.location.origin + "/api/mcp-server", "_blank"); }} size="sm" className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white text-[12px] h-7">Download gtd_mcp_server.py</Button>
            </div>

            <div className="border border-[#E0E0DE] rounded-[4px] p-4">
              <h3 className="text-[14px] font-medium text-[#37352F] mb-2">Step 4: Configure Claude Desktop</h3>
              <p className="text-[13px] text-[#6B6B6B] mb-2">Add this to your <code className="bg-[#E8E8E6] px-1 rounded text-[12px]">claude_desktop_config.json</code>:</p>
              <div className="relative">
                <pre className="bg-[#37352F] text-[#E8E8E6] rounded-[4px] px-3 py-2 text-[12px] font-mono overflow-x-auto whitespace-pre">{JSON.stringify({
  mcpServers: {
    gtd: {
      command: "python3",
      args: ["/path/to/gtd_mcp_server.py"],
      env: {
        GTD_API_URL: window.location.origin,
        GTD_API_KEY: "gdk_your_api_key_here"
      }
    }
  }
}, null, 2)}</pre>
                <button onClick={() => fallbackCopy(JSON.stringify({mcpServers:{gtd:{command:"python3",args:["/path/to/gtd_mcp_server.py"],env:{GTD_API_URL:window.location.origin,GTD_API_KEY:"gdk_your_api_key_here"}}}},null,2))} className="absolute top-2 right-2 text-[#6B6B6B] hover:text-white p-1"><Copy className="w-4 h-4"/></button>
              </div>
            </div>

            <div className="border border-[#E0E0DE] rounded-[4px] p-4">
              <h3 className="text-[14px] font-medium text-[#37352F] mb-2">Step 5: Restart Claude Desktop</h3>
              <p className="text-[13px] text-[#6B6B6B]">After saving the config, restart Claude Desktop. You should see "gtd" in the MCP tools list.</p>
            </div>

            <div className="border border-[#E0E0DE] rounded-[4px] p-4 bg-[#F7F6F3]">
              <h3 className="text-[14px] font-medium text-[#37352F] mb-2">Available Tools</h3>
              <div className="space-y-2 text-[12px]">
                <div><p className="font-medium text-[#37352F] mb-1">Read-only (auto-approved, no confirmation):</p><div className="flex flex-wrap gap-1">{["list_goals","list_projects","list_tasks","get_task","search_tasks","list_people","get_weekly_roster","get_daily_roster","list_habits","habit_stats"].map(t=><span key={t} className="px-1.5 py-0.5 bg-[#D3E5EF] text-[#183B56] rounded-[3px]">{t}</span>)}</div></div>
                <div><p className="font-medium text-[#37352F] mb-1">Create/Add (Claude asks permission):</p><div className="flex flex-wrap gap-1">{["create_goal","create_project","create_task","add_note","add_to_daily_roster","add_to_weekly_roster","log_habit"].map(t=><span key={t} className="px-1.5 py-0.5 bg-[#DBEDDB] text-[#1E4620] rounded-[3px]">{t}</span>)}</div></div>
                <div><p className="font-medium text-[#37352F] mb-1">Update (Claude asks permission):</p><div className="flex flex-wrap gap-1">{["update_task","update_project","update_goal","move_task_to_day"].map(t=><span key={t} className="px-1.5 py-0.5 bg-[#FDECC8] text-[#6B4C13] rounded-[3px]">{t}</span>)}</div></div>
                <div><p className="font-medium text-[#37352F] mb-1">Destructive (Claude asks permission + warns):</p><div className="flex flex-wrap gap-1">{["delete_goal","delete_project","delete_task","delete_habit"].map(t=><span key={t} className="px-1.5 py-0.5 bg-[#FFE2DD] text-[#6E3630] rounded-[3px]">{t}</span>)}</div></div>
              </div>
            </div>
          </div>
        </div>
      </>)}
    </div>
  );
}
